import { useEffect } from "react";
import { getAppVersion } from "../services/dataService";
import { useSnackbar } from "notistack";
import { Button } from "@mui/material";
import { useSetAtom } from "jotai";
import { maintenanceNoticeAtom } from "../atoms/maintenanceNoticeAtom.js";

const INITIAL_DELAY = 20_000; // 20 seconds
const POLLING_INTERVAL = 300_000; // 5 minutes

export const useVersionCheck = () => {
  const { enqueueSnackbar } = useSnackbar();
  const frontendVersion = import.meta.env.VITE_APP_VERSION;
  const frontendCommit = import.meta.env.VITE_APP_COMMIT;
  const frontendBuiltAt = import.meta.env.VITE_APP_BUILT_AT;
  const setMaintenanceNotice = useSetAtom(maintenanceNoticeAtom);

  useEffect(() => {
    let intervalId = null;

    const checkVersion = async () => {
      try {
        const backendData = await getAppVersion();
        if (!backendData) return;

        setMaintenanceNotice(backendData.maintenance_notice || null);

        // on test environments the version often stays the same across deployments,
        // so a changed commit - or a rebuild of the same commit - also counts as a new version
        const versionChanged =
          backendData.version && backendData.version !== frontendVersion;
        const commitChanged =
          backendData.commit && backendData.commit !== frontendCommit;
        const buildChanged =
          backendData.built_at && backendData.built_at !== frontendBuiltAt;

        if (versionChanged || commitChanged || buildChanged) {
          enqueueSnackbar(
            "Es gibt eine neue Version der Anwendung. Bitte speichern Sie Änderungen und laden die Seite neu.",
            {
              key: "version-update_snackbar",
              variant: "info",
              persist: true,
              preventDuplicate: true,
              action: (key) => (
                <Button
                  size="small"
                  onClick={() => window.location.reload()}
                  color={"primaryContrast"}
                >
                  Aktualisieren
                </Button>
              ),
            }
          );
          // Once the notification is shown, we can stop polling.
          if (intervalId) clearInterval(intervalId);
        }
      } catch (error) {
        console.error("Version check failed:", error);
        // Optional: decide if you want to stop polling on error
      }
    };

    // check after the initial delay
    let timeoutId = setTimeout(checkVersion, INITIAL_DELAY);
    intervalId = setInterval(checkVersion, POLLING_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    frontendVersion,
    frontendCommit,
    frontendBuiltAt,
    enqueueSnackbar,
    setMaintenanceNotice,
  ]);

  return frontendVersion;
};
